import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { normalizeTodayTasksCountPayload } from './customer-task-today-count.utils';
import { NotificationWithCustomerTaskDto, PendingNotificationsQuery, PendingNotificationsResponse } from '../core/models';

/**
 * Customer task notifications API.
 *
 * List pending: `GET /api/CustomerTask/notifications/pending?skip=0&take=10`
 * Count pending: `GET /api/CustomerTask/notifications/pending/count`
 * Mark read: `PATCH /api/CustomerTask/notification/{notificationID}`
 * Full URL: `{baseUrl}/CustomerTask/notification/{notificationID}` (baseUrl already includes `/api`).
 * Body: `{ "isRead": true }`
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private static readonly jsonHeaders = new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });
  private static readonly listCacheTtlMs = 5 * 60 * 1000;

  private readonly listCache = new Map<
    string,
    { expiresAt: number; value: PendingNotificationsResponse<NotificationWithCustomerTaskDto> | null }
  >();

  constructor(private readonly http: HttpClient) {}

  getPendingNotifications(
    query: PendingNotificationsQuery = {},
  ): Observable<PendingNotificationsResponse<NotificationWithCustomerTaskDto> | null> {
    const skip = Math.max(0, query.skip ?? 0);
    const take = Math.max(1, query.take ?? 10);
    const forceRefresh = query.forceRefresh === true;
    const url = `${environment.baseUrl}/CustomerTask/notifications/pending?skip=${skip}&take=${take}`;
    const cacheKey = `${skip}:${take}`;
    const cached = this.listCache.get(cacheKey);

    if (!forceRefresh && cached != null && cached.expiresAt > Date.now()) {
      return of(cached.value);
    }

    return this.http.get<PendingNotificationsResponse<NotificationWithCustomerTaskDto> | null>(url, { headers: NotificationsService.jsonHeaders }).pipe(
      map((payload) => {
        const response = payload ?? ({} as PendingNotificationsResponse<NotificationWithCustomerTaskDto>);
        return {
          list: Array.isArray(response.list) ? response.list : [],
          total: typeof response.total === 'number' ? response.total : 0,
          skip: typeof response.skip === 'number' ? response.skip : skip,
          take: typeof response.take === 'number' ? response.take : take,
        };
      }),
      tap((response) => {
        this.listCache.set(cacheKey, {
          value: response,
          expiresAt: Date.now() + NotificationsService.listCacheTtlMs,
        });
      }),
      catchError(() => of(null)),
    );
  }

  getPendingNotificationsCount(): Observable<number | null> {
    const url = `${environment.baseUrl}/CustomerTask/notifications/pending/count`;
    return this.http
      .get<unknown>(url, { headers: NotificationsService.jsonHeaders })
      .pipe(
        map((payload) => normalizeTodayTasksCountPayload(payload)),
        catchError(() => of(null)),
      );
  }

  markNotificationRead(notificationID: string): Observable<unknown | null> {
    const url = `${environment.baseUrl}/CustomerTask/notification/${notificationID}`;
    return this.http
      .patch<unknown>(url, { isRead: true }, { headers: NotificationsService.jsonHeaders })
      .pipe(
        tap((result) => {
          if (result !== null) {
            this.clearNotificationsCache();
          }
        }),
        catchError(() => of(null)),
      );
  }

  clearNotificationsCache(): void {
    this.listCache.clear();
  }
}
